import { useState } from 'react';

// Minimal HTML5 drag-and-drop reordering. `onReorder(newList, movedItem)` fires on drop.
// `group` lets several lists share one hook instance while only allowing drops within the same group
// unless `onMoveAcross(item, fromGroup, toGroup, toIndex)` is provided.
export default function useDragReorder({ onReorder, onMoveAcross } = {}) {
  const [drag, setDrag] = useState(null); // {id, group}
  const [over, setOver] = useState(null); // {id, group}

  const itemProps = (list, item, index, group = 'default') => ({
    draggable: true,
    onDragStart: (e) => { e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', item.id); } catch { /* ie */ } setDrag({ id: item.id, group }); },
    onDragOver: (e) => { if (!drag) return; if (drag.group !== group && !onMoveAcross) return; e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (!over || over.id !== item.id) setOver({ id: item.id, group }); },
    onDragLeave: () => { if (over && over.id === item.id) setOver(null); },
    onDrop: (e) => {
      e.preventDefault(); e.stopPropagation();
      if (!drag) return;
      if (drag.group !== group) { if (onMoveAcross) onMoveAcross(drag.id, drag.group, group, index); }
      else if (drag.id !== item.id) {
        const from = list.findIndex((x) => x.id === drag.id);
        if (from >= 0) { const next = [...list]; const [moved] = next.splice(from, 1); next.splice(index, 0, moved); onReorder(next, moved, group); }
      }
      setDrag(null); setOver(null);
    },
    onDragEnd: () => { setDrag(null); setOver(null); },
  });

  // Props for an empty-area / container drop target (append to end of `group`).
  const containerProps = (list, group = 'default') => ({
    onDragOver: (e) => { if (!drag) return; if (drag.group !== group && !onMoveAcross) return; e.preventDefault(); },
    onDrop: (e) => {
      e.preventDefault();
      if (!drag) return;
      if (drag.group !== group) { if (onMoveAcross) onMoveAcross(drag.id, drag.group, group, list.length); }
      else { const from = list.findIndex((x) => x.id === drag.id); if (from >= 0 && from !== list.length - 1) { const next = [...list]; const [moved] = next.splice(from, 1); next.push(moved); onReorder(next, moved, group); } }
      setDrag(null); setOver(null);
    },
  });

  const isDragging = (id) => !!drag && drag.id === id;
  const isOver = (id) => !!over && over.id === id && (!drag || drag.id !== id);
  return { itemProps, containerProps, isDragging, isOver, dragging: drag };
}
